// BlogAdd.js - Updated with CKEditor Integration
import React, { useReducer, useState, useCallback, useEffect, Fragment } from "react";
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Form,
    FormGroup,
    Label,
    Input,
    Button
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { MdAdd, MdDelete } from "react-icons/md";
// helpers
import handleReducer, { textChange, onBlur, fileChange, formData } from "../lib/handleChange";
import toastAlert from "../lib/toastAlert";
import isEmpty from "../lib/isEmpty";
import { slugify } from "../lib/common";
import fileObjectUrl from "../lib/fileOblectURL";

// api
import { createBlog } from "../api/admin";
// validation
import { addBlogValid } from "../validation/admin.validation";
// components
import ButtonLoader from "../mycomponents/Layout/ButtonLoader";
import AuthorDropdown from "../mycomponents/Layout/SearchDropDown";
import FieldError from "../mycomponents/Layout/FieldError";
import CKEditor from "../mycomponents/CkEditor";

const initialFormValue = {
    title: "",
    slug: "",
    coverImage: null,
    authorId: "",
    category: "",
    readDuration: 1,
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    blogTitles: [""],
    content: ""
};

const BlogAdd = () => {
    const navigate = useNavigate();

    const [formValue, setFormValue] = useReducer(handleReducer, initialFormValue);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [loader, setLoader] = useState(false);

    const {
        title,
        slug,
        coverImage,
        authorId,
        category,
        readDuration,
        metaTitle,
        metaDescription,
        metaKeywords,
        blogTitles,
        content
    } = formValue;

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "title") {
            textChange(e, setFormValue);
            setFormValue({ type: "CHANGE", name: "slug", value: slugify(value) });
        } else if (name?.startsWith('blogTitles')) {
            let splitVal = name?.split('blogTitles');
            let position = parseInt(splitVal[1])
            let jsonParse = JSON.parse(JSON.stringify(blogTitles));
            jsonParse[parseInt(position)] = value;
            setFormValue({ type: "CHANGE", name: "blogTitles", value: jsonParse });
        } else return textChange(e, setFormValue);
    };

    const handleFileChange = (e) => fileChange(e, setFormValue);

    const handleBlur = (e) => {
        const { name } = e.target;
        if (name?.startsWith('blogTitles')) {
            let splitVal = name?.split('blogTitles');
            let position = parseInt(splitVal[1])
            return setTouched({ ...touched, ...{ [`blogTitles${position + 1}`]: true } })
        } return onBlur(e, setTouched)
    };

    const handleContentChange = (event) => {
        const htmlContent = event.editor.getData();
        setFormValue({ type: "CHANGE", name: "content", value: htmlContent });
    };

    const handleContentBlur = () => {
        setTouched({ ...touched, content: true });
    };

    const handleAdd = (e) => {
        setFormValue({ type: "CHANGE", name: "blogTitles", value: [...blogTitles, ...['']] });
    };

    const handleRemove = (position) => {
        if (blogTitles.length === 1) return;
        let value = JSON.parse(JSON.stringify(blogTitles));
        value.splice(parseInt(position), 1);
        setFormValue({ type: "CHANGE", name: "blogTitles", value });
    };

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            try {
                setLoader(true);
                const data = formData(formValue);
                formValue.blogTitles.forEach((item, index) => {
                    data.append(`blogTitles[${index}]`, item);
                });
                const { status: ok, message, errors } = await createBlog(data);
                setLoader(false);

                if (ok === true) {
                    toastAlert("success", message, "blog");
                    setFormValue({ type: "RESET", value: initialFormValue });
                    navigate("/blog-list");
                } else if (errors) {
                    setErrors(errors);
                } else if (message) {
                    toastAlert("error", message, "blog");
                }
            } catch (err) {
                console.log(err, "errrr");
                setLoader(false);
            }
        },
        [formValue, navigate]
    );

    useEffect(() => {
        const { status, errors } = addBlogValid(formValue);
        if (!status) setErrors(errors);
        else setErrors({});
    }, [formValue]);

    // console.log(formValue, 'formValueformValue')
    // console.log(errors, 'errorserrorserrors')
    // console.log(touched, 'touchedtouchedtouched')
    return (
        <Row>
            <Col xl={12}>
                <Card>
                    <CardHeader>Add Blog</CardHeader>
                    <CardBody>
                        <Form>
                            <Row>
                                {/* Left column */}
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Title</Label>
                                        <Input
                                            type="text"
                                            name="title"
                                            value={title}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter title"
                                        />
                                        <FieldError name="title" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Slug</Label>
                                        <Input
                                            type="text"
                                            name="slug"
                                            value={slug}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="auto-generate or custom slug"
                                        />
                                        <FieldError name="slug" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Category</Label>
                                        <Input
                                            type="text"
                                            name="category"
                                            value={category}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter category"
                                        />
                                        <FieldError name="category" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Read Duration (Minutes)</Label>
                                        <Input
                                            type="number"
                                            name="readDuration"
                                            value={readDuration}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter duration"
                                            min={1}
                                        />
                                        <FieldError name="readDuration" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Author</Label>
                                        <AuthorDropdown
                                            value={authorId}
                                            onChange={(id) =>
                                                setFormValue({ type: "CHANGE", name: "authorId", value: id })
                                            }
                                            onBlur={() => setTouched({ ...touched, authorId: true })}
                                        />
                                        <FieldError name="authorId" touched={touched} errors={errors} />
                                    </FormGroup>
                                </Col>

                                {/* Right column */}
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Meta Title</Label>
                                        <Input
                                            type="text"
                                            name="metaTitle"
                                            value={metaTitle}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="SEO title"
                                        />
                                        <FieldError name="metaTitle" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Meta Description</Label>
                                        <Input
                                            type="textarea"
                                            name="metaDescription"
                                            value={metaDescription}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="SEO description"
                                            rows={4}
                                        />
                                        <FieldError
                                            name="metaDescription"
                                            touched={touched}
                                            errors={errors}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Meta Keywords (comma separated)</Label>
                                        <Input
                                            type="textarea"
                                            name="metaKeywords"
                                            value={metaKeywords}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="e.g. blog, seo"
                                            rows={4}
                                        />
                                        <FieldError name="metaKeywords" touched={touched} errors={errors} />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Cover Image</Label>
                                        <Input
                                            type="file"
                                            name="coverImage"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            onBlur={handleBlur}
                                        />
                                        {coverImage && (
                                            <img
                                                src={fileObjectUrl(coverImage)}
                                                width="140px"
                                                alt="preview"
                                                className="mt-2"
                                            />
                                        )}
                                        <FieldError name="coverImage" touched={touched} errors={errors} />
                                    </FormGroup>
                                    ⚠️ Note: Allowed formats: JPG, JPEG, WEBP, PNG. Max size: 5MB. Recommended size: 1920×1080 px. (landscape image )

                                </Col>
                            </Row>

                            <Row>
                                {
                                    blogTitles?.length > 0 && blogTitles?.map((el, key) => {
                                        return (
                                            <Fragment key={key}>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>Blog Title ({key + 1})</Label>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <Input
                                                                type="textarea"
                                                                name={`blogTitles${key}`}
                                                                value={blogTitles && blogTitles[key]}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                placeholder={`Enter blog title (${key + 1})`}
                                                                rows={1}
                                                            />

                                                            <Button
                                                                type="button"
                                                                color="success"
                                                                className="btn-bordered"
                                                                onClick={handleAdd}
                                                            >
                                                                <MdAdd />
                                                            </Button>

                                                            <Button
                                                                type="button"
                                                                color="danger"
                                                                className="btn-bordered"
                                                                onClick={() => handleRemove(key)}
                                                                disabled={blogTitles.length === 1}
                                                            >
                                                                <MdDelete />
                                                            </Button>
                                                        </div>

                                                        {
                                                            touched && touched[`blogTitles${key + 1}`] && (
                                                                <FieldError
                                                                    name={`blogTitles${key + 1}`}
                                                                    touched={touched}
                                                                    errors={errors && errors?.blogTitles}
                                                                />
                                                            )
                                                        }
                                                    </FormGroup>
                                                </Col>
                                            </Fragment>
                                        )
                                    })
                                }
                            </Row>

                            {/* CKEditor Content Section */}
                            <Row>
                                <Col xs={12}>
                                    <FormGroup>
                                        <Label htmlFor="blogContent">Content *</Label>
                                        <CKEditor
                                            value={content}
                                            onChange={handleContentChange}
                                            onBlur={handleContentBlur}
                                        />
                                        <FieldError name="content" touched={touched} errors={errors} />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <ButtonLoader
                                loader={loader}
                                color="primary"
                                text="Submit"
                                onClick={handleSubmit}
                                disabled={!isEmpty(errors)}
                            />
                        </Form>
                    </CardBody>
                </Card>
            </Col>
        </Row >
    );
};

export default BlogAdd;